/**
 * MinIO Documents Section - Displays documents stored in MinIO (TASK-282)
 * Read-only view of documents uploaded through domain-specific uploaders
 *
 * Integrates with:
 * - KYC document uploads (passport, ID card, KYC forms)
 * - Risk Profile PDF uploads
 * - Portfolio uploads
 * - AML/Compliance documents
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText,
  Image,
  Table,
  File,
  Eye,
  Download,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  PenLine,
} from 'lucide-react';
import {
  documentService,
  ClientDocument,
  DocumentType,
  DocumentsByTypeResponse,
} from '../../services/documentService';
import { ensureWorkbenchDocument } from '../../services/api/workbenchImport';
import type { WorkbenchFormat } from '../../services/api/documentApi';
import PDFDocumentViewer from './PDFDocumentViewer';

/** OOXML MIMEs the workbench can edit: .docx (SuperDoc), .xlsx (Univer), .pptx (PPTist). */
const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PPTX_MIME =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const PDF_MIME = 'application/pdf';

/** The workbench format for a customer document, or null if not editable. */
const workbenchFormat = (doc: ClientDocument): WorkbenchFormat | null => {
  const name = doc.filename?.toLowerCase() ?? '';
  if (doc.mime_type === DOCX_MIME || name.endsWith('.docx')) return 'docx';
  if (doc.mime_type === XLSX_MIME || name.endsWith('.xlsx')) return 'xlsx';
  if (doc.mime_type === PPTX_MIME || name.endsWith('.pptx')) return 'pptx';
  // TASK-533: PDF AcroForms open in the fill-and-download workbench.
  if (doc.mime_type === PDF_MIME || name.endsWith('.pdf')) return 'pdf';
  return null;
};

/** Whether a customer document can be opened in the workbench. */
const isEditableInWorkbench = (doc: ClientDocument): boolean =>
  workbenchFormat(doc) !== null;

interface MinIODocumentsSectionProps {
  clientId: string;
  onDocumentCountChange?: (count: number) => void;
}

const MinIODocumentsSection: React.FC<MinIODocumentsSectionProps> = ({
  clientId,
  onDocumentCountChange,
}) => {
  const [documentsData, setDocumentsData] = useState<DocumentsByTypeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTypes, setExpandedTypes] = useState<Set<DocumentType>>(new Set(['kyc', 'risk-profile']));

  // Viewer state
  const [viewingDocumentId, setViewingDocumentId] = useState<string | null>(null);

  // "Edit in Workbench" — which doc is currently being imported/opened.
  // ponytail: route-navigates; on the canvas (TASK-494) a click tears down the
  // grid. Phase-2 (TASK-500): swap navigate() for open_view()/addSpecs to summon
  // the workbench as a tile instead of leaving the canvas.
  const navigate = useNavigate();
  const [openingWorkbenchId, setOpeningWorkbenchId] = useState<string | null>(null);

  // Fetch documents
  useEffect(() => {
    fetchDocuments();
  }, [clientId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await documentService.getClientDocuments(clientId);
      setDocumentsData(data);
      onDocumentCountChange?.(data.total);
    } catch (err) {
      console.error('Failed to fetch MinIO documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const toggleTypeExpanded = (type: DocumentType) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleViewDocument = (document: ClientDocument) => {
    setViewingDocumentId(document.id);
  };

  const handleEditInWorkbench = async (document: ClientDocument) => {
    if (openingWorkbenchId) return;
    setOpeningWorkbenchId(document.id);
    try {
      // Import into the workbench store on first use (idempotent), then open it.
      const format = workbenchFormat(document) ?? 'docx';
      await ensureWorkbenchDocument(document.id, format);
      navigate(`/workbench/${document.id}`);
    } catch (err) {
      console.error('Failed to open document in workbench:', err);
      toast.error('Could not open this document in the workbench.');
    } finally {
      setOpeningWorkbenchId(null);
    }
  };

  const handleDownloadDocument = async (document: ClientDocument) => {
    try {
      const blob = await documentService.downloadDocument(document.id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <File className="w-5 h-5 text-gray-400" />;
    if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
      return <Table className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-400" />;
  };

  const getTypeIcon = (type: DocumentType) => {
    const icons: Record<DocumentType, string> = {
      kyc: '🪪',
      portfolio: '📊',
      'risk-profile': '⚠️',
      aml: '🔍',
      compliance: '📋',
      contracts: '📝',
      general: '📄',
    };
    return icons[type] || '📄';
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-kielis-navy animate-spin mr-2" />
          <span className="text-gray-600">Loading documents...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8 text-red-600">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
          <button
            onClick={fetchDocuments}
            className="ml-4 px-3 py-1 text-sm bg-red-100 hover:bg-red-200 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!documentsData || documentsData.total === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No documents stored yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Documents will appear here after uploading via KYC, Risk Profile, or Portfolio sections
          </p>
        </div>
      </div>
    );
  }

  const documentTypes = Object.keys(documentsData.documents_by_type) as DocumentType[];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-kielis-navy" />
          <h3 className="text-lg font-medium text-gray-900">
            Stored Documents
          </h3>
          <span className="px-2 py-0.5 text-xs font-medium bg-kielis-navy/10 text-kielis-navy rounded-full">
            {documentsData.total}
          </span>
        </div>
        <button
          onClick={fetchDocuments}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh documents"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Documents by Type */}
      <div className="space-y-3">
        {documentTypes.map((type) => {
          const docs = documentsData.documents_by_type[type] || [];
          if (docs.length === 0) return null;

          const isExpanded = expandedTypes.has(type);

          return (
            <div
              key={type}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              {/* Type Header */}
              <button
                onClick={() => toggleTypeExpanded(type)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{getTypeIcon(type)}</span>
                  <span className="font-medium text-gray-900">
                    {documentService.getDocTypeDisplayName(type)}
                  </span>
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {docs.length}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Documents List */}
              {isExpanded && (
                <div className="border-t border-gray-100">
                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.mime_type)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {doc.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {documentService.formatFileSize(doc.file_size)} •{' '}
                            {doc.created_at
                              ? new Date(doc.created_at).toLocaleDateString()
                              : 'Unknown date'}
                            {doc.extraction_confidence !== undefined && (
                              <span
                                className={`ml-2 ${
                                  doc.extraction_confidence > 0.8
                                    ? 'text-green-600'
                                    : doc.extraction_confidence > 0.6
                                    ? 'text-yellow-600'
                                    : 'text-red-600'
                                }`}
                              >
                                • {Math.round(doc.extraction_confidence * 100)}% confidence
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Status badge */}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${documentService.getStatusColor(
                            doc.status
                          )}`}
                        >
                          {doc.status}
                        </span>

                        {/* Edit in Workbench (TASK-449/450/444) — .docx + .xlsx + .pptx */}
                        {isEditableInWorkbench(doc) && (
                          <button
                            onClick={() => handleEditInWorkbench(doc)}
                            disabled={openingWorkbenchId === doc.id}
                            className="p-2 text-gray-500 hover:text-kielis-navy hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
                            title="Edit in Workbench"
                          >
                            {openingWorkbenchId === doc.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <PenLine className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* View button */}
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="p-2 text-gray-500 hover:text-kielis-navy hover:bg-gray-100 rounded-lg transition-colors"
                          title="View document"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Download button */}
                        <button
                          onClick={() => handleDownloadDocument(doc)}
                          className="p-2 text-gray-500 hover:text-kielis-navy hover:bg-gray-100 rounded-lg transition-colors"
                          title="Download document"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* PDF Document Viewer Modal */}
      {viewingDocumentId && (
        <PDFDocumentViewer
          documentId={viewingDocumentId}
          isOpen={true}
          onClose={() => setViewingDocumentId(null)}
        />
      )}
    </div>
  );
};

export default MinIODocumentsSection;
